import React from 'react';
import { motion } from 'framer-motion';
import { Sparkles, ArrowRight } from 'lucide-react';
import { PhoneFrame } from './PhoneFrame';
const SCREEN_IMAGES = {
  challenge: 'https://cdn.magicpatterns.com/uploads/iaLmVqpJ1Vm1ZLwDQz4TP6/Screenshot_2026-05-19_at_13.40.17.png',
  hero: 'https://cdn.magicpatterns.com/uploads/e2N8yB8h9RZL2KLY9mHzNz/Screenshot_2026-05-19_at_13.40.27.png',
  roster: 'https://cdn.magicpatterns.com/uploads/bPpMkikSPYUY7q2hdJLDgj/Screenshot_2026-05-19_at_13.40.35.png',
  caseOpen: 'https://cdn.magicpatterns.com/uploads/kutGFNdNhwrw5bzPPDH2sx/Screenshot_2026-05-19_at_13.40.49.png'
};
export function LayerHollywood() {
  return <section className="py-24 bg-solventum-dark" data-id="element-59">
      <div className="max-w-7xl mx-auto px-6" data-id="element-60">
        <motion.div initial={{
        opacity: 0,
        y: 20
      }} whileInView={{
        opacity: 1,
        y: 0
      }} viewport={{
        once: true
      }} transition={{
        duration: 0.6
      }} className="mb-16" data-id="element-61">
          <div className="flex items-center gap-3 mb-4" data-id="element-62">
            <div className="bg-solventum-green text-solventum-dark px-4 py-1 rounded-full text-sm font-bold" data-id="element-63">
              LAYER 1
            </div>
            <Sparkles className="w-5 h-5 text-solventum-green" data-id="element-64" />
            <span className="text-solventum-lightTeal/60 text-xs tracking-[0.2em] uppercase" data-id="element-65">
              Mobile · Challenge → Hero → Treasure chest → Case open
            </span>
          </div>
          <h2 className="font-editorial text-4xl md:text-5xl font-bold text-white mb-4" data-id="element-66">
            The Peer Learnings Intro
          </h2>
          <p className="text-xl text-solventum-lightTeal/80 max-w-3xl" data-id="element-67">
            A clear three-step entry: a confident hero invitation, a roster of
            real peers to choose from, and then the case itself — opened
            full-bleed with summary, challenge and outcome up front.
          </p>
        </motion.div>

        {/* Mobile mockup stage */}
        <motion.div initial={{
        opacity: 0,
        y: 30
      }} whileInView={{
        opacity: 1,
        y: 0
      }} viewport={{
        once: true
      }} transition={{
        duration: 0.7
      }} className="relative rounded-2xl overflow-hidden ring-1 ring-white/10 bg-gradient-to-br from-[#01403A] via-solventum-dark to-[#011A16] py-16 px-6" data-id="element-68">
          <div className="absolute inset-0 opacity-50" style={{
          backgroundImage: 'radial-gradient(circle at 50% 50%, rgba(5,221,77,0.18) 0%, transparent 55%)'
        }} data-id="element-69" />

          <div className="relative flex flex-col lg:flex-row items-center justify-center gap-5 lg:gap-3" data-id="element-70">
            <PhoneStepCustom label="The challenge" data-id="element-71">
              <ChallengeScreen data-id="element-72" />
            </PhoneStepCustom>
            <FlowArrow data-id="element-73" />
            <PhoneStep label="Hero invitation" image={SCREEN_IMAGES.hero} highlight={false} data-id="element-74" />
            <FlowArrow data-id="element-75" />
            <PhoneStep label="Treasure chest" image={SCREEN_IMAGES.roster} highlight={false} data-id="element-76" />
            <FlowArrow data-id="element-77" />
            <PhoneStep label="Case open" image={SCREEN_IMAGES.caseOpen} highlight data-id="element-78" />
          </div>

          {/* Bottom annotations */}
          <div className="relative mt-14 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 max-w-6xl mx-auto" data-id="element-79">
            <Annotation title="The challenge" body="Every case begins with a challenge — the framing that earns attention." data-id="element-80" />
            <Annotation title="Hero invitation" body="A single, confident promise — see how peers solved theirs — and one call to action." data-id="element-81" />
            <Annotation title="Treasure chest" body="Real clinicians presented as peers. Browse by the face behind the case." data-id="element-82" />
            <Annotation title="Case open" body="Summary, challenge and outcome surfaced immediately — no chrome, no friction." data-id="element-83" />
          </div>
        </motion.div>

        <motion.p initial={{
        opacity: 0
      }} whileInView={{
        opacity: 1
      }} viewport={{
        once: true
      }} transition={{
        duration: 0.6,
        delay: 0.6
      }} className="text-center text-solventum-lightTeal/60 mt-12 italic" data-id="element-84">
          "Oh, that's Dr O'Connor — I want to see how he handled that fracture."
        </motion.p>
      </div>
    </section>;
}
function PhoneStep({
  label,
  image,
  highlight
}: {
  label: string;
  image: string;
  highlight: boolean;
}) {
  return <div className="relative" data-id="element-85">
      <div className={`absolute -top-3 left-1/2 -translate-x-1/2 px-3 py-1 text-[10px] font-bold tracking-[0.2em] uppercase rounded-full z-10 whitespace-nowrap ${highlight ? 'bg-solventum-green text-solventum-dark' : 'bg-white/10 backdrop-blur text-white border border-white/10'}`} data-id="element-86">
        {label}
      </div>
      <PhoneFrame className="!w-[185px] sm:!w-[200px]" data-id="element-87">
        <ImageScreen src={image} alt={label} data-id="element-88" />
      </PhoneFrame>
    </div>;
}
function PhoneStepCustom({
  label,
  children
}: {
  label: string;
  children: React.ReactNode;
}) {
  return <div className="relative" data-id="element-89">
      <div className="absolute -top-3 left-1/2 -translate-x-1/2 px-3 py-1 text-[10px] font-bold tracking-[0.2em] uppercase rounded-full z-10 whitespace-nowrap bg-white/10 backdrop-blur text-white border border-white/10" data-id="element-90">
        {label}
      </div>
      <PhoneFrame className="!w-[185px] sm:!w-[200px]" data-id="element-91">{children}</PhoneFrame>
    </div>;
}
function ChallengeScreen() {
  return <div className="relative w-full h-full bg-solventum-dark overflow-hidden" data-id="element-92">
      {/* Animated vertical line */}
      <motion.div className="absolute top-0 bottom-0 w-px bg-solventum-green/70 origin-center" style={{
      right: '22%'
    }} initial={{
      scaleY: 0,
      opacity: 0
    }} whileInView={{
      scaleY: 1,
      opacity: 1
    }} viewport={{
      once: true,
      amount: 0.4
    }} transition={{
      duration: 1.2,
      delay: 0.3,
      ease: [0.22, 1, 0.36, 1]
    }} aria-hidden="true" data-id="element-93" />

      {/* Long horizontal — draws from left toward vertical */}
      <motion.div className="absolute left-0 h-px bg-solventum-green/70 origin-left" style={{
      bottom: '12%',
      right: '22%'
    }} initial={{
      scaleX: 0,
      opacity: 0
    }} whileInView={{
      scaleX: 1,
      opacity: 1
    }} viewport={{
      once: true,
      amount: 0.4
    }} transition={{
      duration: 1,
      delay: 0.8,
      ease: [0.22, 1, 0.36, 1]
    }} aria-hidden="true" data-id="element-94" />

      {/* Short horizontal — continues to right edge */}
      <motion.div className="absolute h-px bg-solventum-green/70 origin-left" style={{
      bottom: '12%',
      left: '78%',
      right: 0
    }} initial={{
      scaleX: 0,
      opacity: 0
    }} whileInView={{
      scaleX: 1,
      opacity: 1
    }} viewport={{
      once: true,
      amount: 0.4
    }} transition={{
      duration: 0.7,
      delay: 1.2,
      ease: [0.22, 1, 0.36, 1]
    }} aria-hidden="true" data-id="element-95" />

      {/* Bottom-right horizontal accent */}
      <motion.div className="absolute h-px bg-solventum-green/70 origin-right" style={{
      bottom: '6%',
      right: 0,
      width: '22%'
    }} initial={{
      scaleX: 0,
      opacity: 0
    }} whileInView={{
      scaleX: 1,
      opacity: 1
    }} viewport={{
      once: true,
      amount: 0.4
    }} transition={{
      duration: 0.6,
      delay: 1.4,
      ease: [0.22, 1, 0.36, 1]
    }} aria-hidden="true" data-id="element-96" />

      {/* Logo */}
      <div className="relative z-10 pt-3 px-4" data-id="element-97">
        <img src="https://cdn.magicpatterns.com/uploads/qHCwY7wts3zVy5vRBDiXqD/solventum-logo-vert-clr-rev-rgb.png" alt="Solventum" className="h-4 w-auto" data-id="element-98" />
      </div>

      {/* Headline */}
      <div className="relative z-10 px-4 mt-16" data-id="element-99">
        <h3 className="text-white font-semibold leading-[1.1] tracking-tight text-[19px]" style={{
        fontFamily: "'Inter', sans-serif"
      }} data-id="element-100">
          Every case begins with a challenge.
        </h3>
      </div>
    </div>;
}
function ImageScreen({
  src,
  alt
}: {
  src: string;
  alt: string;
}) {
  return <div className="relative w-full h-full bg-solventum-dark" data-id="element-101">
      <img src={src} alt={alt} className="absolute inset-0 w-full h-full object-contain object-top" data-id="element-102" />
    </div>;
}
function FlowArrow() {
  return <div className="flex items-center justify-center" data-id="element-103">
      <div className="w-10 h-10 rounded-full bg-solventum-green/15 ring-1 ring-solventum-green/40 flex items-center justify-center" data-id="element-104">
        <ArrowRight className="w-4 h-4 text-solventum-green hidden lg:block" data-id="element-105" />
        <Sparkles className="w-4 h-4 text-solventum-green lg:hidden" data-id="element-106" />
      </div>
    </div>;
}
function Annotation({
  title,
  body
}: {
  title: string;
  body: string;
}) {
  return <div className="max-w-[280px]" data-id="element-107">
      <div className="text-solventum-green text-[10px] font-semibold tracking-[0.25em] uppercase mb-2" data-id="element-108">
        {title}
      </div>
      <div className="text-solventum-lightTeal/70 text-sm leading-relaxed" data-id="element-109">
        {body}
      </div>
    </div>;
}